import { useEffect, useRef, RefObject } from "react";

/**
 * Attaches an IntersectionObserver to the returned ref.
 * When the element enters the viewport, the "visible" class is added.
 * Works with the reveal / count-up / draw-line CSS classes in index.css.
 */
const useScrollAnimation = <T extends HTMLElement = HTMLElement>(
  threshold = 0.12
): RefObject<T> => {
  const containerRef = useRef<T>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const targets = el.querySelectorAll(
      ".reveal, .reveal-left, .reveal-right, .count-up, .draw-line"
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold }
    );

    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [threshold]);

  return containerRef;
};

export default useScrollAnimation;
